import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { SetlistCard } from '../components/SetlistCard';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { useAsync } from '../hooks/useAsync';
import { normalizeText } from '../lib/format';
import { getSetlists } from '../services/catalog';

const PAGE_SIZE = 12;
const filterLabels = { artist: '아티스트', venue: '공연장', region: '지역', festival: '페스티벌', date: '날짜' } as const;
const sorts = { performance: '최신 공연순', created: '최근 등록순', artist: '아티스트 가나다순', songs: '곡 수 많은 순', likes: '좋아요 많은 순', bookmarks: '저장 많은 순', views: '조회 많은 순' } as const;

export function SetlistsPage() {
  const state = useAsync(getSetlists, [], 'setlists');
  const [params, setParams] = useSearchParams();
  const artist = params.get('artist') ?? '';
  const venue = params.get('venue') ?? '';
  const region = params.get('region') ?? '';
  const festival = params.get('festival') ?? '';
  const date = params.get('date') ?? '';
  const requestedSort = params.get('sort') ?? 'performance';
  const sort = Object.hasOwn(sorts, requestedSort) ? requestedSort as keyof typeof sorts : 'performance';
  const requestedPage = Number(params.get('page') ?? 1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const values = [...(state.data ?? [])].filter((item) =>
      (!artist || normalizeText(item.artist_name).includes(normalizeText(artist))) &&
      (!venue || normalizeText(item.venue_name ?? '').includes(normalizeText(venue))) &&
      (!region || normalizeText(item.region ?? '').includes(normalizeText(region))) &&
      (!festival || normalizeText(item.festival_name ?? '').includes(normalizeText(festival))) &&
      (!date || item.performance_date === date)
    );
    values.sort((a, b) => sort === 'created'
      ? b.created_at.localeCompare(a.created_at)
      : sort === 'artist'
        ? a.artist_name.localeCompare(b.artist_name, 'ko') || b.performance_date.localeCompare(a.performance_date)
        : sort === 'songs'
          ? b.song_count - a.song_count || b.performance_date.localeCompare(a.performance_date)
          : sort === 'likes'
            ? b.like_count - a.like_count || b.performance_date.localeCompare(a.performance_date)
            : sort === 'bookmarks'
              ? b.bookmark_count - a.bookmark_count || b.performance_date.localeCompare(a.performance_date)
              : sort === 'views'
                ? b.view_count - a.view_count || b.performance_date.localeCompare(a.performance_date)
                : b.performance_date.localeCompare(a.performance_date));
    return values;
  }, [state.data, artist, venue, region, festival, date, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const page = Math.min(pageCount, Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1);
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const activeFilters = Object.entries(filterLabels).filter(([key]) => params.get(key)?.trim());
  const update = (key: string, value: string) => setParams((previous) => {
    const next = new URLSearchParams(previous);
    if (value) next.set(key, value); else next.delete(key);
    next.delete('page');
    return next;
  }, { replace: true });
  const reset = () => setParams((previous) => {
    const next = new URLSearchParams(previous);
    Object.keys(filterLabels).forEach((key) => next.delete(key));
    next.delete('page');
    return next;
  }, { replace: true });
  const changePage = (nextPage: number) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (nextPage === 1) next.delete('page'); else next.set('page', String(nextPage));
      return next;
    });
    resultsRef.current?.scrollIntoView({ block: 'start' });
    resultsRef.current?.focus({ preventScroll: true });
  };

  return (
    <section className="section page-section archive-page">
      <PageHeader eyebrow="Setlist archive" title="선곡표" description="다시 보고 싶은 공연, 한 곡씩 찾아보세요." action={<Link className="button button-primary" to="/setlists/new">+ 선곡표 등록</Link>} />
      <div className="archive-search-panel">
        <div className="archive-search-row">
          <label className="archive-search"><span>아티스트로 찾기</span><input type="search" value={artist} onChange={(event) => update('artist', event.target.value)} placeholder="어떤 아티스트의 공연을 찾으세요?" /></label>
          <button className={`archive-filter-toggle ${filtersOpen ? 'is-open' : ''}`} type="button" aria-expanded={filtersOpen} aria-controls="setlist-filters" onClick={() => setFiltersOpen((value) => !value)}>상세 필터 <span aria-hidden="true">{filtersOpen ? '−' : '+'}</span></button>
        </div>
        <div className="archive-filters" id="setlist-filters" hidden={!filtersOpen}>
          <label><span>날짜</span><input type="date" value={date} onChange={(event) => update('date', event.target.value)} /></label>
          <label><span>공연장</span><input value={venue} onChange={(event) => update('venue', event.target.value)} placeholder="예: KSPO DOME" /></label>
          <label><span>지역</span><input value={region} onChange={(event) => update('region', event.target.value)} placeholder="예: 서울" /></label>
          <label><span>페스티벌</span><input value={festival} onChange={(event) => update('festival', event.target.value)} placeholder="페스티벌명" /></label>
        </div>
        {activeFilters.length > 0 && <div className="active-filters" aria-label="적용 중인 필터">{activeFilters.map(([key, label]) => <button key={key} onClick={() => update(key, '')} aria-label={`${label}: ${params.get(key)} 필터 해제`}>{label} · {params.get(key)} <span aria-hidden="true">×</span></button>)}<button className="filter-reset" onClick={reset}>모두 지우기</button></div>}
      </div>
      <div className="result-toolbar archive-toolbar" ref={resultsRef} tabIndex={-1}>
        <p role="status">{state.loading ? '기록을 불러오는 중' : state.error ? '기록을 확인해주세요' : <><strong>{filtered.length.toLocaleString('ko-KR')}</strong>개의 기록{activeFilters.length > 0 && <small>전체 {state.data?.length.toLocaleString('ko-KR')}개</small>}</>}</p>
        <label className="archive-sort"><span className="sr-only">선곡표 정렬</span><select value={sort} onChange={(event) => update('sort', event.target.value)}>{Object.entries(sorts).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      </div>
      {state.loading ? <LoadingState /> : state.error ? <ErrorState error={state.error} onRetry={state.reload} /> : visible.length ? (
        <>
          <div className="setlist-grid">{visible.map((item) => <SetlistCard key={item.id} item={item} />)}</div>
          {pageCount > 1 && <nav className="pagination" aria-label="선곡표 페이지"><button disabled={page === 1} onClick={() => changePage(page - 1)}>← 이전</button><span aria-live="polite">{page} / {pageCount}</span><button disabled={page === pageCount} onClick={() => changePage(page + 1)}>다음 →</button></nav>}
        </>
      ) : <EmptyState title="조건에 맞는 선곡표가 없어요" description="검색어를 짧게 바꾸거나 필터를 줄여보세요. 아직 없는 공연은 직접 기록할 수 있습니다." action={<div className="empty-actions"><button className="button button-secondary" onClick={reset}>필터 초기화</button><Link className="button button-primary" to="/setlists/new">새 기록 등록</Link></div>} />}
    </section>
  );
}
